import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useRouter } from '@/routing/navigation'
import Link from '@/routing/Link'
import { AlertCircle, ArrowLeft, Building, Expand, Globe, Loader2, Lock, Pencil, Plus, RotateCcw, Share2, SlidersHorizontal, Store, X } from 'lucide-react'
import { PageContent } from '@/components/layout/PageContent'
import { PageLayout } from '@/components/layout/PageLayout'
import { organizationsApi } from '@/lib/api'
import { blockedFeatureMessage } from '@/lib/blocked-feature-error'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AudienceTagsSection } from '@/layouts/AudienceImportSessionLayout/components/AudienceTagsSection'
import { AddFilterModal } from '@/layouts/AudienceImportSessionLayout/components/AddFilterModal'
import { AudienceMetadataForm } from '@/layouts/AudienceImportSessionLayout/components/AudienceMetadataForm'
import { NarrativeCardEditor } from '@/layouts/AudienceImportSessionLayout/components/NarrativeCardEditor'
import { CoverPhotoGallery } from '@/layouts/AudienceImportSessionLayout/components/CoverPhotoGallery'
import AudienceDetailContent from '@/pages/AudiencesPage/components/AudienceDetailContent'
import { useAudienceStageForm } from '@/pages/AudiencesPage/hooks/useAudienceStageForm'
import { useAudienceSharing, type SharingMode } from '@/pages/AudiencesPage/hooks/useAudienceSharing'
import ShareAudienceDialog from '@/pages/AudiencesPage/components/ShareAudienceDialog'
import RecipientShareDialog from '@/pages/AudiencesPage/components/RecipientShareDialog'
import PublishConfirmDialog from '@/pages/AudiencesPage/components/PublishConfirmDialog'
import { audienceTierDisplayName } from '@/lib/billing/display-names'
import { populationsApi } from '@/lib/api'
import { toast } from 'sonner'
import { CategoryDropdown } from '@/pages/AudiencesPage/components/AudienceFilterPanel'
import { getIndustryDisplayName } from '@/lib/constants/industry-categories'
import { useProfile } from '@/lib/hooks'
import {
  AudienceFilterEditorPanel,
  getLockedFilterRemovalMessage,
  useAudienceFilterEditor,
} from '@/components/audiences/AudienceFilterEditorPanel'
import {
  audienceTagConditionsToPopulationFilter,
  extractIndustrySelection,
} from './audience-edit-stage-helpers'
import { applyFilterCustomLabels } from '@/components/audiences/audience-filter-editor-utils'
import { canPublishAudienceToMarketplace } from '@/components/audiences/audience-sharing-permissions'
import {
  shouldShowOwnerShareControls,
  shouldShowRecipientShareControls,
} from '@/pages/AudiencesPage/utils/audience-share-routing'
import type { AudienceTag, AudienceTagCondition } from '@digital-twins/shared-types'
import type { ColumnGroupMetadata } from '@/types/routes/tests'
import type { SegmentFilterQuestion } from '@/lib/utils/segment-filter-questions'

const SHARING_LABELS: Record<SharingMode, { label: string; icon: typeof Lock }> = {
  private: { label: 'Private', icon: Lock },
  shared: { label: 'Shared', icon: Globe },
  organization: { label: 'Organization', icon: Building },
  marketplace: { label: 'Marketplace', icon: Store },
  published: { label: 'Published', icon: Share2 },
}

interface ColumnMeta {
  name: string
  displayLabel?: string
  groupDisplayLabel?: string
  canonicalId?: string | null
  category?: string
  uniqueValues?: Array<string>
  valueLabels?: Record<string, string>
  isOperational?: boolean
}

export default function EditAudienceStagePage() {
  const { id: audienceId = '' } = useParams<{ id: string }>()
  return <AudienceStageEditor audienceId={audienceId} />
}

export function AudienceStageEditor({ audienceId, onClose, hidePreview }: { audienceId: string; onClose?: () => void; hidePreview?: boolean }) {
  const { t } = useTranslation('app')
  const router = useRouter()
  const { data: profile } = useProfile()
  const [isAgency, setIsAgency] = useState(false)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [recipientShareDialogOpen, setRecipientShareDialogOpen] = useState(false)
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [previewExpanded, setPreviewExpanded] = useState(false)
  const [editAudienceTags, setEditAudienceTags] = useState<Array<AudienceTag>>([])
  const [tagsDirty, setTagsDirty] = useState(false)

  const handleTagsChange = useCallback((tags: Array<AudienceTag>) => {
    setEditAudienceTags(tags)
    setTagsDirty(true)
  }, [])

  const handlePreviewTag = useCallback(
    (conditions: Array<AudienceTagCondition>, signal: AbortSignal) =>
      populationsApi.previewFilter(
        audienceId,
        audienceTagConditionsToPopulationFilter(conditions),
        undefined,
        signal,
      ),
    [audienceId],
  )

  useEffect(() => {
    if (!previewExpanded) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewExpanded(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [previewExpanded])

  const {
    audience,
    loading,
    error,
    values,
    isDirty,
    isSaving,
    isGeneratingCovers,
    coverPhotoOptions,
    respondentCount,
    narrativeCardDraft,
    narrativeCardStatus,
    update,
    updateNarrativeCard,
    generateCoverPhotos,
    saveAll,
  } = useAudienceStageForm(audienceId)

  const showOwnerShare = shouldShowOwnerShareControls({
    mayEdit: audience?.canEdit === true,
    canManageOrgSharing: audience?.canManageOrgSharing,
    audienceOrganizationToken: audience?.organizationToken,
    viewerOrganizationToken: profile?.organizationToken,
  })
  const showRecipientShare = shouldShowRecipientShareControls({
    canManageOrgSharing: audience?.canManageOrgSharing,
  })

  const sharing = useAudienceSharing(showOwnerShare ? audienceId : null)

  // Sync audience tags from API once audience loads. Filter out malformed
  // entries (legacy data could be corrupted to empty arrays) so they never get
  // rendered or re-persisted on the next save.
  useEffect(() => {
    if (audience?.audienceTags) {
      const sanitized = (audience.audienceTags as Array<AudienceTag>).filter(
        (t): t is AudienceTag =>
          Boolean(t) &&
          typeof t === 'object' &&
          !Array.isArray(t) &&
          typeof t.id === 'string' &&
          Array.isArray(t.conditions),
      )
      setEditAudienceTags(sanitized)
      setTagsDirty(false)
    }
  }, [audience?.audienceTags])

  // Redirect non-managers back to the audience detail page.
  useEffect(() => {
    if (!loading && audience && audience.canEdit === false) {
      router.push(`/audiences/${audienceId}`)
    }
  }, [loading, audience, audienceId, router])

  useEffect(() => {
    organizationsApi.getCurrent().then((org) => {
      if (org?.type === 'agency') setIsAgency(true)
    }).catch(() => {})
  }, [])

  const isPlatformAdmin = profile?.isPlatformAdmin === true
  const canPublishMarketplace = canPublishAudienceToMarketplace(profile)

  const [industryTokens, setIndustryTokens] = useState<Array<string>>(() => {
    return extractIndustrySelection(audience).tokens
  })
  const [taxonomyDirty, setTaxonomyDirty] = useState(false)
  const tokenNameMap = useRef<Map<string, string>>(
    extractIndustrySelection(audience).tokenNames,
  )
  const [tokenNameVersion, setTokenNameVersion] = useState(0)
  const prevAudienceRef = useRef(audience)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filterAudience, setFilterAudience] = useState<typeof audience | null>(null)
  const [filterColumns, setFilterColumns] = useState<Array<ColumnMeta>>([])
  const [filterableColumns, setFilterableColumns] = useState<Array<ColumnMeta>>([])
  const [filterGroups, setFilterGroups] = useState<Array<ColumnGroupMetadata>>([])
  const [selectedFilterColumns, setSelectedFilterColumns] = useState<Array<string> | null>(null)
  const [filterCustomLabels, setFilterCustomLabels] = useState<Record<string, string>>({})
  const [filterLoadError, setFilterLoadError] = useState<string | null>(null)
  const [filtersLoading, setFiltersLoading] = useState(false)

  // Synchronous state adjustment: when audience reference changes, derive tokens immediately
  // (eliminates the one-render-cycle delay that caused "No industry selected" to flash — RND-2476)
  if (audience !== prevAudienceRef.current) {
    prevAudienceRef.current = audience
    const selection = extractIndustrySelection(audience)
    if (selection.tokens.length > 0 && !taxonomyDirty) {
      setIndustryTokens(selection.tokens)
      tokenNameMap.current = selection.tokenNames
      setTokenNameVersion((v) => v + 1)
    }
  }

  // Async fallback: if the detail-for-ui endpoint returned empty industryLabels,
  // try fetching from the list endpoint which may include additional label sources.
  useEffect(() => {
    if (!audience?.id) return
    if (industryTokens.length > 0) return
    if (taxonomyDirty) return

    let cancelled = false

    async function hydrateFromListPayload() {
      try {
        const audiences = await populationsApi.list({ limit: 200 })
        if (cancelled) return
        const listAudience = audiences.find((item) => item.id === audience!.id)
        const fallbackSelection = extractIndustrySelection(listAudience)
        if (fallbackSelection.tokens.length > 0) {
          setIndustryTokens(fallbackSelection.tokens)
          tokenNameMap.current = fallbackSelection.tokenNames
          setTokenNameVersion((v) => v + 1)
          setTaxonomyDirty(false)
        }
      } catch {
        // Fallback fetch failure is non-critical; industry stays empty.
      }
    }

    void hydrateFromListPayload()
    return () => { cancelled = true }
  }, [audience?.id, industryTokens.length, taxonomyDirty])

  const handleToggleIndustry = useCallback((token: string) => {
    setTaxonomyDirty(true)
    setIndustryTokens((prev) =>
      prev.includes(token) ? prev.filter((t) => t !== token) : [...prev, token],
    )
  }, [])

  const industrySummary = useMemo(() => {
    if (industryTokens.length === 0) return undefined
    return industryTokens
      .map((t) => getIndustryDisplayName(tokenNameMap.current.get(t) ?? '') ?? tokenNameMap.current.get(t) ?? t)
      .join(', ')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industryTokens, tokenNameVersion])

  const selectedIndustryLabels = useMemo(
    () =>
      industryTokens.map((token) => {
        const name =
          getIndustryDisplayName(tokenNameMap.current.get(token) ?? '') ??
          tokenNameMap.current.get(token) ??
          token
        return { token, name, fullPath: name, tier: 0 }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [industryTokens, tokenNameVersion],
  )

  const filterEditor = useAudienceFilterEditor({
    audience: filterAudience,
    columns: filterColumns,
    groups: filterGroups,
    selectedColumns: selectedFilterColumns,
    onSelectedColumnsChange: setSelectedFilterColumns,
    customLabels: filterCustomLabels,
    onCustomLabelsChange: setFilterCustomLabels,
  })

  const [hideColumnsModalOpen, setHideColumnsModalOpen] = useState(false)

  // Use the filterable-only column set (no includeAllColumns) so "hidden" means
  // "was in the wizard's filterable set but not currently active" — not every raw dataset column.
  const filterableColumnKeys = useMemo(
    () => new Set(filterableColumns.map((c) => c.name)),
    [filterableColumns],
  )

  // Collapsed hidden-filter entries: group members → single chip per group,
  // ungrouped columns → individual chips.
  const hiddenFilterItems = useMemo(() => {
    const isHidden = (col: string) =>
      filterableColumnKeys.has(col) &&
      !filterEditor.activeFilterColumns.includes(col)

    const groupMemberColumns = new Set(
      filterEditor.filterGroups.flatMap((g) => g.memberColumns),
    )

    type HiddenEntry =
      | { type: 'single'; sourceColumn: string; displayLabel: string }
      | { type: 'group'; groupId: string; label: string; memberColumns: Array<string> }

    const result: Array<HiddenEntry> = []
    const seenGroups = new Set<string>()

    // Add one entry per group where at least one member is hidden
    for (const group of filterEditor.filterGroups) {
      const hiddenMembers = group.memberColumns.filter(isHidden)
      if (hiddenMembers.length > 0 && !seenGroups.has(group.groupId)) {
        seenGroups.add(group.groupId)
        result.push({ type: 'group', groupId: group.groupId, label: group.label, memberColumns: hiddenMembers })
      }
    }

    // Add individual chips for hidden columns NOT in any group
    for (const item of filterEditor.filterChipItems) {
      if (item.isLocked) continue
      if (groupMemberColumns.has(item.sourceColumn)) continue
      if (!isHidden(item.sourceColumn)) continue
      result.push({ type: 'single', sourceColumn: item.sourceColumn, displayLabel: item.displayLabel })
    }

    return result
  }, [filterEditor.filterChipItems, filterEditor.filterGroups, filterEditor.activeFilterColumns, filterableColumnKeys])

  // Active, hidable (non-locked) questions — uses allQuestions to preserve groups + categories.
  const hidableQuestions = useMemo(() => {
    const lockedCols = new Set(
      filterEditor.filterChipItems
        .filter((c) => c.isLocked)
        .map((c) => c.sourceColumn),
    )
    const activeCols = new Set(filterEditor.activeFilterColumns)
    const isHidable = (col: string) =>
      filterableColumnKeys.has(col) && activeCols.has(col) && !lockedCols.has(col)

    const out: Array<SegmentFilterQuestion> = []
    for (const q of filterEditor.allQuestions) {
      if (q.kind === 'single') {
        if (isHidable(q.options[0]?.sourceColumn)) out.push(q)
      } else {
        const opts = q.options.filter((o) => isHidable(o.sourceColumn))
        if (opts.length > 0) {
          out.push(opts.length === q.options.length ? q : { ...q, options: opts })
        }
      }
    }
    return out
  }, [filterEditor.allQuestions, filterEditor.filterChipItems, filterEditor.activeFilterColumns, filterableColumnKeys])

  // Sample values for the tag modal — use the full column set so every column
  // in the question picker (built from filterColumns) has matching values.
  // Fall back to valueLabels keys when uniqueValues is empty (e.g. DB timeout).
  const columnSamples = useMemo(
    () =>
      Object.fromEntries(
        filterColumns.map((col) => {
          const vals = col.uniqueValues ?? []
          if (vals.length > 0) return [col.name, vals]
          const fromLabels = col.valueLabels ? Object.keys(col.valueLabels) : []
          return [col.name, fromLabels]
        }),
      ),
    [filterColumns],
  )

  const columnValueLabels = useMemo(
    () =>
      Object.fromEntries(
        filterColumns
          .filter((col) => col.valueLabels && Object.keys(col.valueLabels).length > 0)
          .map((col) => [col.name, col.valueLabels!]),
      ),
    [filterColumns],
  )

  const ensureFiltersLoaded = useCallback(async () => {
    if (!audienceId || filterAudience?.id === audienceId) return

    setFiltersLoading(true)
    setFilterLoadError(null)
    try {
      const [fullAudience, meta, filterableMeta] = await Promise.all([
        populationsApi.get(audienceId),
        populationsApi.getColumnMetadata(audienceId, { includeAllColumns: true }),
        populationsApi.getColumnMetadata(audienceId),
      ])
      setFilterAudience(fullAudience as typeof audience)
      setFilterColumns(meta.columns ?? [])
      setFilterableColumns(filterableMeta.columns ?? [])
      setFilterGroups(meta.groups ?? [])
      setSelectedFilterColumns(
        Array.isArray(fullAudience.activeFilterColumns)
          ? fullAudience.activeFilterColumns
          : null,
      )
      setFilterCustomLabels({})
    } catch (err) {
      console.error('Failed to load audience filters:', err)
      setFilterLoadError('Could not load this audience filters.')
    } finally {
      setFiltersLoading(false)
    }
  }, [audienceId, filterAudience?.id])

  const openFilterModal = useCallback(() => {
    setFilterModalOpen(true)
    void ensureFiltersLoaded()
  }, [ensureFiltersLoaded])

  useEffect(() => {
    void ensureFiltersLoaded()
  }, [ensureFiltersLoaded])

  const handleSave = async () => {
    try {
      if (filterEditor.hasChanges) {
        const lockedRemovalMessage = getLockedFilterRemovalMessage(filterEditor)
        if (lockedRemovalMessage) {
          toast.error(lockedRemovalMessage)
          return
        }
        if (!filterAudience) {
          toast.error('Audience filters are still loading')
          return
        }
      }

      if (taxonomyDirty) {
        await populationsApi.replaceLabels(audienceId, {
          industryLabels: industryTokens,
        })
      }
      if (filterEditor.hasChanges && filterAudience) {
        if (Object.keys(filterCustomLabels).length > 0) {
          const updatedMappings = applyFilterCustomLabels(
            filterAudience.segmentationMappings ?? [],
            filterCustomLabels,
            filterEditor.filterGroups,
          )
          await populationsApi.updateSegmentationMappings(audienceId, updatedMappings)
        }
        await populationsApi.updateActiveFilterColumns(
          audienceId,
          filterEditor.activeFilterColumns,
        )
      }
      if (tagsDirty) {
        await populationsApi.update(audienceId, {
          audienceTags: editAudienceTags,
        })
        setTagsDirty(false)
      }
      await saveAll()
      router.push(`/audiences/${audienceId}`)
    } catch (err) {
      const blocked = blockedFeatureMessage(err)
      const message = blocked ?? (err instanceof Error ? err.message : String(err))
      console.error('Failed to save audience changes:', message)
      toast.error(blocked ? message : `Failed to save changes: ${message}`)
    }
  }

  const handlePublish = async () => {
    setPublishDialogOpen(false)
    await sharing.updateMode('marketplace')
  }

  const selectedCoverUrl =
    coverPhotoOptions.length > 0
      ? coverPhotoOptions[values.selectedCoverPhotoIndex]?.url ?? null
      : audience?.coverImageUrl ?? null

  if (loading) {
    return (
      <PageLayout className="min-h-screen max-w-7xl pt-14 py-8 md:pt-8">
        <PageContent>
          <Skeleton className="h-8 w-48 mt-8 md:mt-0" />
          <Skeleton className="h-10 w-64" />
          <div className="flex gap-6">
            <Skeleton className="h-[600px] flex-1 rounded-2xl" />
            <Skeleton className="h-[600px] flex-1 rounded-2xl" />
          </div>
        </PageContent>
      </PageLayout>
    )
  }

  if (!audience) {
    return (
      <PageLayout variant="wide" className="min-h-screen py-8">
        <PageContent>
          <Text color="muted">Audience not found or you don&apos;t have permission to edit it.</Text>
          <Link href="/audiences">
            <Button variant="link" className="px-0">{t('audiences.backToAudiences')}</Button>
          </Link>
        </PageContent>
      </PageLayout>
    )
  }

  const sharingLabel = showRecipientShare
    ? (audience.orgGrantScope === 'specific'
      ? { label: 'Specific people', icon: Globe }
      : audience.orgGrantScope === 'org'
        ? { label: 'Organization', icon: Building }
        : { label: 'Only me', icon: Lock })
    : (SHARING_LABELS[sharing.sharingMode] ?? SHARING_LABELS.private)
  const SharingIcon = sharingLabel.icon

  return (
    <PageLayout variant="wide" className="min-h-screen pb-8 pt-14 md:pt-8">
      <PageContent className="gap-8 min-w-0">
        {/* Header row */}
        <div className="flex items-end justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full"
              onClick={() => router.push(`/audiences/${audienceId}`)}
              data-testid="edit-audience-back-button"
              aria-label={t('audiences.backToAudience')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Heading level={1} className="font-display text-4xl tracking-tight">
              {t('audiences.editTitle')}
            </Heading>
          </div>

          {isAgency && (
            sharing.sharingMode === 'marketplace' ? (
              <Badge variant="outline" className="gap-2 border-primary/30 bg-primary/10 text-primary px-4 py-2 text-sm">
                <Globe className="h-4 w-4" />
                {t('audienceEdit.publishedMarketplace')}
              </Badge>
            ) : (
              <Button
                onClick={() => setPublishDialogOpen(true)}
                variant="cta"
                className="gap-2"
              >
                <Store className="h-4 w-4" />
                {t('audienceEdit.publishMarketplace')}
              </Button>
            )
          )}
        </div>

        {error && (
          <Alert variant="destructive" className="rounded-xl">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Two-column layout */}
        <div className="flex flex-col gap-8 lg:flex-row lg:gap-6 min-w-0">
          {/* Left column: form */}
          <div className="flex flex-1 min-w-0 flex-col gap-6">
            {/* Sharing permits & tier */}
            <div className="flex flex-wrap items-start gap-8">
              {/* Sharing permits */}
              <div className="flex flex-col gap-2">
                <Text size="sm" weight="medium" color="muted">
                  Sharing permits
                </Text>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="gap-1.5 border-0 rounded bg-primary/10 text-[#0A2120] dark:text-primary px-3 py-1 text-base"
                  >
                    <SharingIcon className="h-3.5 w-3.5" />
                    {sharingLabel.label}
                  </Badge>
                  {(showOwnerShare || showRecipientShare) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        if (showRecipientShare) {
                          setRecipientShareDialogOpen(true)
                          return
                        }
                        setShareDialogOpen(true)
                      }}
                      data-testid="edit-sharing-button"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <Text size="sm" color="muted">
                  {sharing.sharingMode === 'private' && 'This audience is restricted. Only you can use it.'}
                  {sharing.sharingMode === 'shared' && 'This audience is shared with selected collaborators.'}
                  {sharing.sharingMode === 'marketplace' && 'This audience is shared in the marketplace. Everyone has access to it.'}
                </Text>
              </div>

              {/* Audience tier — only visible for platform admins on marketplace audiences */}
              {sharing.sharingMode === 'marketplace' && isPlatformAdmin && (
                <div className="flex flex-col gap-2" data-testid="audience-tier-section">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Audience tier
                  </Label>
                  <Select
                    value={values.tier}
                    onValueChange={(value) => update('tier', value)}
                  >
                    <SelectTrigger
                      className="w-[180px] rounded-xl border-border"
                      data-testid="audience-tier-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tier1" data-testid="audience-tier-option-tier1">
                        {audienceTierDisplayName('tier1')}
                      </SelectItem>
                      <SelectItem value="tier2" data-testid="audience-tier-option-tier2">
                        {audienceTierDisplayName('tier2')}
                      </SelectItem>
                      <SelectItem value="tier3" data-testid="audience-tier-option-tier3">
                        {audienceTierDisplayName('tier3')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Metadata form */}
            <AudienceMetadataForm
              values={values}
              onChange={update}
            />

            {/* Industry */}
            <div className="flex flex-col gap-1">
              <label className="text-sm leading-[1.5] text-muted-foreground">
                {t('audiences.industry')}
              </label>
              <div className="flex min-h-[38px] items-center justify-between gap-3">
                <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                  {industryTokens.length === 0 ? (
                    <span className="text-base text-muted-foreground">{t('audiences.noIndustry')}</span>
                  ) : (
                    industryTokens.map((token) => {
                      const name = getIndustryDisplayName(tokenNameMap.current.get(token) ?? '') ?? tokenNameMap.current.get(token) ?? token
                      return (
                        <span
                          key={token}
                          className="inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-sm text-foreground transition-colors hover:bg-primary/20"
                        >
                          {name}
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); handleToggleIndustry(token) }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleToggleIndustry(token) } }}
                            className="rounded-full p-0.5 cursor-pointer hover:bg-destructive/20"
                            aria-label={t('audiences.removeIndustry', { name })}
                          >
                            <X className="h-2.5 w-2.5" />
                          </span>
                        </span>
                      )
                    })
                  )}
                </div>
                <CategoryDropdown
                  selectedTokens={industryTokens}
                  onToggle={handleToggleIndustry}
                  categorySummary={industrySummary}
                  tokenNameMap={tokenNameMap}
                  onNamesRegistered={() => setTokenNameVersion((v) => v + 1)}
                  renderTrigger={
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 gap-2"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {t('audiences.addIndustry')}
                    </Button>
                  }
                />
              </div>
            </div>

            {/* Audience subsegments editor */}
            <div className="border-t border-[#f1f1f1] dark:border-border/40 pt-4">
              <AudienceTagsSection
                compact
                tags={editAudienceTags}
                onTagsChange={handleTagsChange}
                columnSamples={columnSamples}
                columnValueLabels={columnValueLabels}
                questions={filterEditor.allQuestions}
                disabled={filtersLoading || filterColumns.length === 0}
                onPreviewTag={handlePreviewTag}
              />
            </div>

            {/* Filters to hide */}
            <div className="border-t border-[#f1f1f1] dark:border-border/40 pt-4 flex flex-col gap-1">
              <Text size="sm" color="muted">{t('audienceCreate.filtersToHide')}</Text>
              <TooltipProvider delayDuration={200}>
                <div className="flex min-h-[38px] items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                    {hiddenFilterItems.length === 0 && (
                      <span className="text-base text-muted-foreground">{t('audiences.noColumnsHidden')}</span>
                    )}
                    {hiddenFilterItems.map((entry) => (
                      <Tooltip key={entry.type === 'group' ? entry.groupId : entry.sourceColumn}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="flex items-center gap-2 rounded-[6px] bg-primary/15 pl-4 pr-2 py-1.5 text-left hover:opacity-80 transition-opacity max-w-[300px]"
                            onClick={() => {
                              if (entry.type === 'group') {
                                entry.memberColumns.forEach((col) => filterEditor.addFilter(col))
                              } else {
                                filterEditor.addFilter(entry.sourceColumn)
                              }
                            }}
                          >
                            <span className="truncate text-base font-medium text-black dark:text-foreground">
                              {entry.type === 'group' ? entry.label : entry.displayLabel}
                            </span>
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full hover:bg-black/10 text-black/50 hover:text-black transition-colors dark:text-foreground/50 dark:hover:text-foreground">
                              <X className="h-3.5 w-3.5" />
                            </span>
                          </button>
                        </TooltipTrigger>
                        {entry.type === 'group' ? (
                          <TooltipContent side="bottom" align="start" className="max-w-sm text-[14px] px-4 py-3">
                            {entry.label}
                          </TooltipContent>
                        ) : (
                          <TooltipContent side="bottom" align="start" className="max-w-sm text-[14px] px-4 py-3">
                            {entry.sourceColumn}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-2"
                    onClick={() => setHideColumnsModalOpen(true)}
                    disabled={filtersLoading || hidableQuestions.length === 0}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('audiences.hideColumn')}
                  </Button>
                </div>
              </TooltipProvider>
            </div>

            <div className="border-t border-[#f1f1f1] dark:border-border/40" />

            {/* Cover photo gallery */}
            <div className="flex flex-col gap-2">
              <Text size="sm" color="muted">
                {t('audiences.marketplaceCover')}
              </Text>
              {isGeneratingCovers ? (
                <CoverPhotoGallery
                  options={[]}
                  selectedIndex={0}
                  onSelect={() => {}}
                  itemClassName="h-[99px]"
                />
              ) : coverPhotoOptions.length > 0 ? (
                <>
                  <CoverPhotoGallery
                    options={coverPhotoOptions.map(o => ({ url: o.url, thumbnailUrl: o.thumbnailUrl ?? o.url }))}
                    selectedIndex={values.selectedCoverPhotoIndex}
                    onSelect={(index) => update('selectedCoverPhotoIndex', index)}
                    itemClassName="h-[99px]"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void generateCoverPhotos()}
                    className="w-fit border-[#0A2120] text-[#0A2120] dark:border-input dark:text-foreground"
                  >
                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                    {t('audiences.regeneratePhotos')}
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => void generateCoverPhotos()}
                  className="w-fit"
                >
                  {t('audiences.generateCoverPhotos')}
                </Button>
              )}
            </div>

            {/* Narrative card editor */}
            <div className="border-t border-[#f1f1f1] dark:border-border/40 pt-4">
              <Text size="sm" color="muted" className="mb-3">
                {t('audiences.storyProfileInsights')}
              </Text>
              <NarrativeCardEditor
                draft={narrativeCardDraft}
                status={narrativeCardStatus}
                onUpdate={updateNarrativeCard}
                onRetry={() => {}}
              />
            </div>

          </div>

          {/* Right column: preview */}
          <div className={`flex flex-1 min-w-0 flex-col gap-3 ${hidePreview ? 'hidden' : ''}`}>
            <div className="flex items-start justify-between">
              <Text size="sm" weight="medium" color="muted">
                {t('audiences.preview')}
              </Text>
              <button
                onClick={() => setPreviewExpanded(true)}
                className="inline-flex items-center gap-1.5 rounded border-[1.5px] border-[#0A2120] dark:border-primary px-3 py-1 text-sm font-medium text-[#0A2120] dark:text-primary hover:bg-[#0A2120]/10 dark:hover:bg-primary/10 transition-colors"
              >
                <Expand className="h-3.5 w-3.5" />
                {t('audiences.seeFullSize')}
              </button>
            </div>
            <div className="rounded-2xl">
              <AudienceDetailContent
                audienceId={audienceId}
                variant="edit-preview"
                previewOverrides={{
                  name: values.name,
                  tagline: values.tagline || undefined,
                  titleFont: values.titleFont,
                  coverImageUrl: selectedCoverUrl,
                  respondentCount,
                  audienceType: audience?.audienceType ?? null,
                  industryLabels: selectedIndustryLabels,
                  narrativeCard: narrativeCardDraft,
                }}
              />
            </div>
          </div>
        </div>

      </PageContent>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 z-30 bg-background py-4">
        <div className="flex items-center justify-center gap-4 px-8">
          <Button
            onClick={handleSave}
            disabled={isSaving || (!isDirty && !taxonomyDirty && !filterEditor.hasChanges && !tagsDirty)}
            variant="cta"
            className="rounded-lg px-8 h-11 text-base"
            data-testid="edit-audience-save-button"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('audienceCreate.saving')}
              </>
            ) : (
              t('audienceCreate.saveChanges')
            )}
          </Button>
          <Button
            variant="outline"
            className="rounded-lg px-8 h-11 text-base border-[#0A2120] text-[#0A2120] dark:border-input dark:text-foreground"
            onClick={() => router.push(`/audiences/${audienceId}`)}
            disabled={isSaving}
            data-testid="edit-audience-cancel-button"
          >
            {t('audienceCreate.cancel')}
          </Button>
        </div>
      </div>

      {/* Full-size preview overlay */}
      {previewExpanded && (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-background/80 backdrop-blur-sm"
          onClick={() => setPreviewExpanded(false)}
          role="presentation"
        >
          <div
            className="relative w-full max-w-[1302px] my-10 mx-4"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t('audiences.fullSizePreview')}
          >
            <AudienceDetailContent
              audienceId={audienceId}
              variant="modal"
              onClose={() => setPreviewExpanded(false)}
              previewOverrides={{
                name: values.name,
                tagline: values.tagline || undefined,
                titleFont: values.titleFont,
                coverImageUrl: selectedCoverUrl,
                respondentCount,
                audienceType: audience?.audienceType ?? null,
                industryLabels: selectedIndustryLabels,
                narrativeCard: narrativeCardDraft,
              }}
            />
          </div>
        </div>
      )}

      {/* Dialogs */}
      <AddFilterModal
        open={hideColumnsModalOpen}
        onOpenChange={setHideColumnsModalOpen}
        questions={hidableQuestions}
        onAdd={(question: SegmentFilterQuestion) => {
          question.options.forEach((o) => {
            if (
              filterableColumnKeys.has(o.sourceColumn) &&
              filterEditor.activeFilterColumns.includes(o.sourceColumn)
            ) {
              filterEditor.removeFilter(o.sourceColumn)
            }
          })
        }}
        title={t('audienceCreate.hideAColumn')}
        addButtonLabel={t('audiences.hideColumn')}
      />

      <Dialog open={filterModalOpen} onOpenChange={setFilterModalOpen}>
        <DialogContent className="max-h-[85vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit audience filters</DialogTitle>
            <DialogDescription>
              Add or remove the filters shown for this audience. Changes are saved with the main audience form.
            </DialogDescription>
          </DialogHeader>

          {filtersLoading ? (
            <div className="flex flex-col gap-4 py-2">
              <Skeleton className="h-20 w-full rounded-2xl" />
              <Skeleton className="h-40 w-full rounded-2xl" />
            </div>
          ) : filterLoadError ? (
            <Alert variant="destructive" className="rounded-xl">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{filterLoadError}</AlertDescription>
            </Alert>
          ) : filterAudience ? (
            <AudienceFilterEditorPanel editor={filterEditor} className="border-0 p-0 shadow-none" />
          ) : (
            <Text size="sm" color="muted">
              Open the editor to load audience filters.
            </Text>
          )}
        </DialogContent>
      </Dialog>

      {showOwnerShare && (
        <ShareAudienceDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          audienceId={audienceId}
          audienceName={values.name}
          sharing={sharing}
          canPublishMarketplace={canPublishMarketplace}
        />
      )}
      {showRecipientShare && (
        <RecipientShareDialog
          open={recipientShareDialogOpen}
          onOpenChange={setRecipientShareDialogOpen}
          audienceId={audienceId}
          audienceName={values.name}
          currentScope={audience.orgGrantScope ?? 'org'}
        />
      )}

      <PublishConfirmDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        audienceId={audienceId}
        audienceName={values.name}
        onConfirm={handlePublish}
      />
    </PageLayout>
  )
}
